/**
 * "Where {name} stands today" — the Support at Home starting-position anchor, between the case
 * facts and the mode chooser (owner direction, 2026-08-25: the insight panel's opening story —
 * current position, which test binds, and the restructure targets — belongs on the page, as a
 * headline with the depth behind taps, not as a wall of text).
 *
 * This is the SAH sibling of the aged care module's walk-in note: the position every strategy
 * below is implicitly compared against, stated where the reader is, and it cannot be closed.
 *
 * CALCULATION DISCIPLINE — the figures are the section's own `beforeFinancials` (the same snapshot
 * the contribution panel and Financial Impact read), and the targets come from
 * `calculateSahOptimizationTargets` — the SAME function the insight panel calls, extracted so the
 * two surfaces share one computation and can never disagree.
 */
import { anyoneOwnsHome, assessPensionHomeowner } from '../../../../shared/src/calculation/householdPosition.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import React from 'react';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, PlainWordsBeat } from '../../components/narrative/PlainWords.tsx';
import {
    calculateSahOptimizationTargets,
    resolveIllnessSeparatedForSahInsight,
    type SahOptimizationData,
} from './supportAtHomePensionInsightUtils.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

interface SahStartingPositionProps {
    factFind: DraftFactFind;
    before: {
        socialSecurity?: number;
        assessableAssets?: number;
        totalAssessableIncome?: number;
        cashFlow?: number;
        socialSecurityBreakdown?: Array<{ label: string; value: string }>;
    } | null;
    /**
     * The starting CASHFLOW, and how long savings last at it — the second of the three figures the
     * panel beside this note reports.
     *
     * Owner-caught, 2026-08-27: this note spoke only to the Age Pension, while the panel it anchors
     * reports pension, cashflow AND estate. A reader given one number out of three anchors on the
     * one they were given, and for a household in deficit the cashflow is the one that decides
     * whether the plan is affordable at all.
     */
    runwayHeadline?: string | null;
    runwayDetail?: string | null;
    /** What the estate is projected to be with no strategy, and over how long. */
    startingLegacy?: number | null;
    legacyYears?: number;
    /** Lets the reader change the period here and watch the estate move (owner, 25 Sep 2026). */
    onLegacyYearsChange?: (years: number) => void;
    /**
     * The selected service plan's contribution — the share of care costs the client pays — and
     * the plan's name. Given, the cashflow beat nets it, because a cashflow that ignores the plan
     * the reader just selected is the figure the owner could not find (2026-09-24). Null on the
     * illustrative package, where no dollar is quoted.
     */
    careContribution?: { amount: number; planName: string | null } | null;
}

const SahStartingPosition = ({
    factFind,
    before,
    runwayHeadline = null,
    runwayDetail = null,
    startingLegacy = null,
    legacyYears = 5,
    onLegacyYearsChange,
    careContribution = null,
}: SahStartingPositionProps) => {
    if (!before) return null;
    /** Cashflow after the service plan's contribution — what the heading and its tone are judged on. */
    const cashFlowNet = before.cashFlow == null ? null : before.cashFlow - (careContribution?.amount ?? 0);

    const v = householdVoice(factFind);
    const name = v.names;
    const isAre = v.are;
    const isCouple = factFind?.adviceFor === 'couple';
    const isHomeowner = anyoneOwnsHome(factFind as unknown as FactFind);
    /*
     * A village or granny-flat resident is assessed on the "special residence" rule, and the page
     * says so (owner, 7 Oct 2026: "have you tested a client living in a granny flat ... or a
     * retirement village"): below the extra allowable amount they are a non-homeowner and what they
     * paid is a counted asset (a village resident is also paid Rent Assistance on the fees); above
     * it they are a homeowner and it is exempt.
     */
    const position = assessPensionHomeowner(factFind as unknown as FactFind, {});
    const residencyNote = position.rvResident
        ? position.rvIsHomeowner
            ? `The retirement village entry contribution (${formatCurrency(position.rvEntryContribution)}) is above the extra allowable amount, so ${name} ${isAre} assessed as a homeowner and it is not counted.`
            : `The retirement village entry contribution (${formatCurrency(position.rvEntryContribution)}) is at or below the extra allowable amount, so ${name} ${isAre} assessed as a non-homeowner: it is counted as an asset, and Rent Assistance is paid on the village fees.`
        : position.grannyFlatResident
            ? position.grannyFlatIsHomeowner
                ? `The granny flat interest is above the extra allowable amount, so ${name} ${isAre} assessed as a homeowner and what was paid for it is not counted.`
                : `The granny flat interest is at or below the extra allowable amount, so ${name} ${isAre} assessed as a non-homeowner and what was paid for it is counted as an asset.`
            : null;
    const illnessSeparated = resolveIllnessSeparatedForSahInsight(factFind, before as never, undefined, false);

    const data: SahOptimizationData = calculateSahOptimizationTargets({
        assessableAssets: before.assessableAssets || 0,
        assessableIncome: before.totalAssessableIncome || 0,
        currentPension: before.socialSecurity || 0,
        isCouple,
        isHomeowner,
        illnessSeparatedForPension: illnessSeparated,
    });

    const pension = data.currentPension;
    const fullTarget = data.targets.find((t) => t.kind === 'full');
    const atFullPension = data.isAssetTestLimiting && (fullTarget?.reductionNeeded ?? 0) < 1 && pension > 0;

    const bindingWhy = data.isAssetTestLimiting
        ? `Both means tests are worked out and the LOWER answer wins. Here the assets test gives ${formatCurrency(data.pensionFromAssetTest)} a year and the income test ${formatCurrency(data.pensionFromIncomeTest)}, so it's the assets test setting the pension. That matters for strategy: moves that reduce COUNTED ASSETS — ${isHomeowner ? 'money into the exempt home, ' : ''}an annuity (only 60% counted), gifting within the free areas, funeral bonds — are the ones that lift it. Income-side moves won't, until the assets test stops binding.`
        : `Both means tests are worked out and the LOWER answer wins. Here the income test gives ${formatCurrency(data.pensionFromIncomeTest)} a year against the assets test's ${formatCurrency(data.pensionFromAssetTest)}, so it's the INCOME test setting the pension. That matters for strategy: reducing counted assets alone won't lift it — what counts is reducing assessable income (for example, an annuity's deduction amount, or restructuring income-producing investments).`;

    /*
     * Bullet-point examples of shifting the dial (owner copy pass, 2026-08-25: "smaller steps" was
     * confusing; these are worked examples, said as such). Rendered with line breaks - the jargon
     * expansion box is whitespace-pre-line.
     */
    const stepTargets = data.targets.filter((t) => t.kind === 'increase' && t.achievable && (t.reductionNeeded ?? 0) > 0);
    const shiftExamples = [
        ...(fullTarget && fullTarget.achievable && (fullTarget.reductionNeeded ?? 0) > 0
            ? [`\u2022 The full pension: shift about ${formatCurrency(fullTarget.reductionNeeded || 0)} \u2192 ${formatCurrency(fullTarget.targetPension)} a year (up ${formatCurrency(fullTarget.increase)}).`]
            : []),
        ...stepTargets.slice(0, 2).map(
            (t) =>
                `\u2022 A ${t.label.replace('Increase by ', '')} lift: shift about ${formatCurrency(t.reductionNeeded || 0)} \u2192 ${formatCurrency(t.targetPension)} a year (up ${formatCurrency(t.increase)}).`,
        ),
    ].join('\n');
    const stepsText = shiftExamples
        ? `These are worked examples from the assets-test taper, not recommendations \u2014 they show the size of shift needed:\n${shiftExamples}\nThe strategies below are how the shifting actually happens \u2014 and the same shifts lower the contribution rates.`
        : '';

    return (
        /*
         * ONE DESIGN LANGUAGE ACROSS THE MODULES (owner, 2026-08-27: "where Baldo stands today looks
         * and feels very different to the Aged Care module").
         *
         * This said the same kinds of thing as the Aged Care walk-in note — where the client stands,
         * and what the strategies below are for — but as bolded run-in prose, so there was nothing
         * to scan and no way to fold it away once read. It now uses the shared PlainWordsBeat and
         * the shared collapse, so the two modules read as one product.
         *
         * The tones carry meaning, exactly as they do in Aged Care: `info` for the assessment (a
         * rule, not a result), `good` where there is genuinely room to improve, `neutral` where
         * there is not. Nothing here is amber: none of these beats is a "look at this".
         */
        <PlainWordsCard eyebrow={`Where ${v.names} stand${v.s} today`} className="mb-0" collapsible>
            <PlainWordsBeat tone="info" heading="Age Pension today — what sets it">
                <span className="font-semibold text-slate-900">{formatCurrency(pension)}/yr</span>. Centrelink counts{' '}
                <span className="font-semibold text-slate-900">{formatCurrency(data.currentAssets)}</span> of assets and{' '}
                <span className="font-semibold text-slate-900">{formatCurrency(data.currentIncome)}/yr</span> of income —
                and it's the <span className="font-semibold text-slate-900">{data.isAssetTestLimiting ? 'assets test' : 'income test'}</span>{' '}
                setting the pension <JargonTerm term="(why does that matter?)">{bindingWhy}</JargonTerm>
                {residencyNote && <> {residencyNote}</>}
            </PlainWordsBeat>

            {atFullPension ? (
                <PlainWordsBeat tone="good" heading="Already at the full pension">
                    {v.Names} {v.are} already at the full pension — nothing to gain there, so the strategies below are
                    about <span className="font-semibold text-slate-900">lowering the contribution rates</span> and
                    improving cashflow.
                </PlainWordsBeat>
            ) : data.isAssetTestLimiting && fullTarget && !fullTarget.achievable ? (
                <PlainWordsBeat tone="neutral" heading="The pension is out of reach — the rates are not">
                    Realistically the pension is out of reach here — it would take shifting more than the counted
                    assets. But shifting assets{' '}
                    <span className="font-semibold text-slate-900">still lowers the contribution rates</span>, so the
                    strategies below are about paying less for care, cashflow and the estate.
                </PlainWordsBeat>
            ) : data.isAssetTestLimiting && fullTarget && (fullTarget.reductionNeeded ?? 0) > 0 ? (
                <PlainWordsBeat tone="good" heading="Room to move — the pension and the rates together">
                    Shifting counted assets{' '}
                    <span className="font-semibold text-slate-900">lifts the Age Pension</span> and{' '}
                    <span className="font-semibold text-slate-900">lowers the Support at Home contribution rates</span>{' '}
                    at the same time — the full pension is about{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(fullTarget.reductionNeeded || 0)}</span>{' '}
                    of shifting away{stepsText && (
                        <>
                            {' '}
                            <JargonTerm term="(what shifting assets could do ›)">{stepsText}</JargonTerm>
                        </>
                    )}
                </PlainWordsBeat>
            ) : !data.isAssetTestLimiting ? (
                <PlainWordsBeat tone="neutral" heading="The income test is what limits the pension">
                    Because the income test is what's limiting the pension, reducing assets alone won't lift it —
                    strategies that reduce <span className="font-semibold text-slate-900">counted income</span> are
                    what matter for the pension here (though reducing assets can still lower the{' '}
                    <span className="font-semibold text-slate-900">contribution rates</span>).
                </PlainWordsBeat>
            ) : null}

            {/*
              * THE OTHER TWO NUMBERS (owner-caught, 2026-08-27). The panel beside this note reports
              * pension, cashflow and estate; the note spoke only to the pension, so a reader anchored
              * on a third of the picture. For a household already in deficit the cashflow is the
              * figure that decides whether any of this is affordable, and the estate is what most
              * families are actually weighing.
              *
              * Each beat is omitted rather than guessed at when its figure could not be computed —
              * silence beats a placeholder, which is the rule this module keeps arriving at.
              */}
            {(before.cashFlow != null || runwayHeadline) && (
                <PlainWordsBeat
                    tone={(cashFlowNet ?? 0) < 0 ? 'warn' : 'good'}
                    heading={
                        (cashFlowNet ?? 0) < 0
                            ? 'Cashflow today — income does not cover it'
                            : 'Cashflow today — income covers it'
                    }
                >
                    {before.cashFlow != null && (
                        <>
                            Money in, less money out, is{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(before.cashFlow)}</span> a year
                            before any care costs.{' '}
                        </>
                    )}
                    {careContribution && before.cashFlow != null && careContribution.amount > 0 && (
                        <>
                            {careContribution.planName ? `“${careContribution.planName}”` : 'The service plan'} costs{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(careContribution.amount)}</span> a year in
                            contributions, leaving{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(before.cashFlow - careContribution.amount)}</span>.{' '}
                        </>
                    )}
                    {careContribution && before.cashFlow != null && careContribution.amount === 0 && (
                        <>
                            At these rates {careContribution.planName ? `“${careContribution.planName}”` : 'the service plan'} costs
                            nothing in contributions, so that figure stands.{' '}
                        </>
                    )}
                    {runwayHeadline && (
                        <>
                            <span className="font-semibold text-slate-900">{runwayHeadline}.</span>
                            {runwayDetail ? ` ${runwayDetail}` : ''}
                        </>
                    )}
                </PlainWordsBeat>
            )}

            {startingLegacy != null && (
                <PlainWordsBeat
                    tone="neutral"
                    heading={onLegacyYearsChange ? (
                        <ProjectionYearsInput years={legacyYears} onChange={onLegacyYearsChange} before="Estate after" after=" — with no plan" />
                    ) : `Estate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'} — with no plan`}
                >
                    On today&apos;s position and no strategy, the projection leaves{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(startingLegacy)}</span>. That is the
                    figure every strategy below is measured against, not today&apos;s net worth — the care is happening
                    either way, so the comparison has to be with doing it unplanned.
                </PlainWordsBeat>
            )}

            <p className="mt-2 text-[11px] text-slate-400">
                The same assessment sets the contribution rates shown on the right. The strategies below exist to move
                these numbers.
            </p>
        </PlainWordsCard>
    );
};

export default SahStartingPosition;
